import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  UsersIcon,
  PlusIcon,
  TrashIcon,
  EnvelopeIcon,
  XMarkIcon,
  ClockIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const Team = () => {
  const { user, isTenantOwner } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, invitationsRes] = await Promise.all([
        api.get('/team/members'),
        api.get('/team/invitations'),
      ]);
      setMembers(membersRes.data.members);
      setInvitations(invitationsRes.data.invitations);
    } catch (error) {
      console.error('Error fetching team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setIsInviting(true);
    try {
      await api.post('/team/invite', { email: inviteEmail, role: inviteRole });
      toast.success('Invitation sent successfully!');
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    try {
      await api.delete(`/team/members/${userId}`);
      toast.success('Team member removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.put(`/team/members/${userId}/role`, { role: newRole });
      toast.success('Role updated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update role');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await api.delete(`/team/invitations/${invitationId}`);
      toast.success('Invitation cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: 'bg-primary-100 text-primary-800',
      admin: 'bg-purple-100 text-purple-800',
      member: 'bg-blue-100 text-blue-800',
    };
    return styles[role] || 'bg-neutral-100 text-neutral-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  const pendingInvitations = invitations.filter(i => i.status === 'pending');

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-orange">
              <UsersIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="page-title">Team Management</h1>
              <p className="text-sm text-neutral-500">{members.length} member{members.length !== 1 ? 's' : ''} in your organization</p>
            </div>
          </div>
          <button onClick={() => setShowInviteModal(true)} className="btn-primary btn-sm flex items-center gap-2">
            <PlusIcon className="w-4 h-4" />
            Invite Member
          </button>
        </div>
      </div>

      {/* Team Members */}
      <div className="card">
        <h3 className="text-lg font-semibold text-neutral-800 mb-6">Team Members</h3>
        <div className="space-y-4">
          {members.map((member) => (
            <div key={member.id} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">
                    {member.firstName?.charAt(0)}{member.lastName?.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-800">
                    {member.firstName} {member.lastName}
                    {member.id === user?.id && <span className="text-neutral-400 ml-1">(You)</span>}
                  </p>
                  <p className="text-xs text-neutral-500">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadge(member.role)}`}>
                  {member.role}
                </span>
                <div className="flex items-center gap-1">
                  {member.emailVerified ? (
                    <CheckCircleIcon className="w-4 h-4 text-green-500" title="Email verified" />
                  ) : (
                    <ClockIcon className="w-4 h-4 text-yellow-500" title="Email not verified" />
                  )}
                </div>
                {isTenantOwner && member.role !== 'owner' && member.id !== user?.id && (
                  <div className="flex items-center gap-2">
                    <select
                      value={member.role}
                      onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                      className="text-xs border border-neutral-200 rounded-md px-2 py-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="p-1 text-red-400 hover:text-red-600 transition-colors"
                      title="Remove member"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-neutral-800 mb-6">Pending Invitations</h3>
          <div className="space-y-3">
            {pendingInvitations.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <EnvelopeIcon className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-800">{invite.email}</p>
                    <p className="text-xs text-neutral-500">
                      Invited as {invite.role} {invite.invitedBy && `by ${invite.invitedBy}`}
                      {' '}&middot; Expires {new Date(invite.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleCancelInvitation(invite.id)}
                  className="p-1 text-neutral-400 hover:text-red-600 transition-colors"
                  title="Cancel invitation"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <EnvelopeIcon className="w-5 h-5 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-800">Invite Team Member</h3>
              </div>
              <button onClick={() => setShowInviteModal(false)} className="p-2 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="input-label">Email Address</label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="input-field"
                  placeholder="colleague@company.com"
                />
              </div>
              <div>
                <label className="input-label">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="input-field"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-xs text-neutral-500 mt-1">
                  {inviteRole === 'admin' ? 'Admins can invite members and manage subscriptions.' : 'Members can access workspaces and use chatbots.'}
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowInviteModal(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" disabled={isInviting} className="btn-primary flex-1">
                  {isInviting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="spinner spinner-sm border-white/30 border-t-white" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    'Send Invitation'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
